import { z } from 'zod';

// =============================================================================
// Contact channel + consent source
// =============================================================================

/**
 * How the patient wants to be contacted for proactive clinic outreach
 * (appointment confirmations, reminders, waitlist openings, refill
 * pickup notifications, lab result availability).
 *
 * `none` means the patient has explicitly declined all proactive contact.
 * It is NOT the default for "no consent captured yet" — that state is
 * represented by the absence of a preferences row.
 */
export const contactChannelSchema = z.enum(['sms', 'voice', 'none']);
export type ContactChannel = z.infer<typeof contactChannelSchema>;

/**
 * Where the consent was captured. Used to decide how authoritative the
 * row is and for audit/compliance.
 */
export const consentSourceSchema = z.enum(['intake_form', 'portal', 'staff', 'voice_call']);
export type ConsentSource = z.infer<typeof consentSourceSchema>;

// =============================================================================
// Preferred time window (voice callback)
// =============================================================================

/**
 * Rough callback window. Intentionally coarse — the voice agent extracts
 * what the patient said ("afternoons after 2") into the structured
 * fields it's confident about and dumps the rest into `notes`.
 *
 * The `notes` field preserves the patient's own words for staff review
 * and for later re-enrichment when a more precise schema is needed.
 */
export const preferredTimeWindowSchema = z.object({
  timeOfDay: z.enum(['morning', 'afternoon', 'evening', 'anytime']).optional(),
  daysOfWeek: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])).optional(),
  notes: z.string().max(200).optional(),
});
export type PreferredTimeWindow = z.infer<typeof preferredTimeWindowSchema>;

// =============================================================================
// Response shape
// =============================================================================

export const patientContactPreferenceSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid(),
  // DB column is NOT NULL with a CHECK constraint — channel is always
  // one of 'sms' / 'voice' / 'none' on any row we return.
  channel: contactChannelSchema,
  phone: z.string().nullable(),
  preferredTimeWindow: preferredTimeWindowSchema.nullable(),
  consentGivenAt: z.string().datetime().nullable(),
  consentSource: consentSourceSchema.nullable(),
  lastConfirmedAt: z.string().datetime().nullable(),
  interactionId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PatientContactPreference = z.infer<typeof patientContactPreferenceSchema>;

// =============================================================================
// Save request (API + internal both use this)
// =============================================================================

/**
 * Write a patient contact preference. Used by:
 *   - Staff UI:      POST /api/patient-preferences
 *   - Voice agent:   POST /internal/patient-preferences
 *   - Portal:        POST /api/patient-preferences (when built)
 *
 * The endpoint upserts: if an active row exists for this patient in this
 * tenant it is soft-deleted and a new row inserted. Current state is
 * always a single active row per (patient, org).
 */
export const savePatientPreferenceRequestSchema = z.object({
  patientId: z.string().uuid(),
  channel: contactChannelSchema,
  phone: z
    .string()
    .min(1)
    .optional()
    .describe('E.164 format. Required when channel is sms or voice.'),
  preferredTimeWindow: preferredTimeWindowSchema.optional(),
  consentSource: consentSourceSchema.describe('Where this preference was captured.'),
  interactionId: z
    .string()
    .uuid()
    .optional()
    .describe('Voice-captured only — links the preference to the interaction that captured it.'),
});
export type SavePatientPreferenceRequest = z.infer<typeof savePatientPreferenceRequestSchema>;

/**
 * Internal variant — same shape plus `organizationId`, since internal
 * routes bypass the session/tenant middleware and need the tenant on
 * the body.
 */
export const saveInternalPatientPreferenceRequestSchema = savePatientPreferenceRequestSchema.extend(
  {
    organizationId: z.string().uuid(),
  }
);
export type SaveInternalPatientPreferenceRequest = z.infer<
  typeof saveInternalPatientPreferenceRequestSchema
>;

// =============================================================================
// Confirm-only request — "the patient reconfirmed their existing
// preference." Bumps `last_confirmed_at` without writing a new consent.
// =============================================================================

export const confirmPatientPreferenceRequestSchema = z.object({
  patientId: z.string().uuid(),
});
export type ConfirmPatientPreferenceRequest = z.infer<typeof confirmPatientPreferenceRequestSchema>;

/**
 * Internal variant of the confirm request. Same shape as the public
 * one plus `organizationId` on the body (internal routes bypass
 * session/tenant middleware).
 */
export const confirmInternalPatientPreferenceRequestSchema =
  confirmPatientPreferenceRequestSchema.extend({
    organizationId: z.string().uuid(),
  });
export type ConfirmInternalPatientPreferenceRequest = z.infer<
  typeof confirmInternalPatientPreferenceRequestSchema
>;
